import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CYRA_LOGO } from "@/lib/brand";
import {
  LayoutDashboard, IdCard, Briefcase, Store, CalendarCog, GraduationCap,
  Wallet, BarChart3, ShieldCheck, Users, ScrollText, LogOut, Menu, X, Activity, Zap,
  CalendarClock, MessageSquare, Scale,
} from "lucide-react";

const NAV = {
  professional: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/pass", label: "Krida Pass", icon: IdCard },
    { to: "/app/work", label: "Krida Work", icon: Briefcase },
    { to: "/app/availability", label: "Availability", icon: CalendarClock },
    { to: "/app/skills", label: "Krida Skills", icon: GraduationCap },
    { to: "/app/pay", label: "Krida Pay", icon: Wallet },
    { to: "/app/messages", label: "Messages", icon: MessageSquare },
    { to: "/app/disputes", label: "Disputes", icon: Scale },
    { to: "/app/intelligence", label: "Intelligence", icon: BarChart3 },
  ],
  organisation: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/work", label: "Krida Work", icon: Briefcase },
    { to: "/app/marketplace", label: "Marketplace", icon: Store },
    { to: "/app/ops", label: "Krida Ops", icon: CalendarCog },
    { to: "/app/directory", label: "Workforce", icon: Users },
    { to: "/app/pay", label: "Krida Pay", icon: Wallet },
    { to: "/app/messages", label: "Messages", icon: MessageSquare },
    { to: "/app/disputes", label: "Disputes", icon: Scale },
    { to: "/app/intelligence", label: "Intelligence", icon: BarChart3 },
  ],
  admin: [
    { to: "/app", label: "Command Centre", icon: LayoutDashboard, end: true },
    { to: "/app/verifications", label: "Verifications", icon: ShieldCheck },
    { to: "/app/directory", label: "Workforce", icon: Users },
    { to: "/app/disputes", label: "Disputes", icon: Scale },
    { to: "/app/intelligence", label: "Intelligence", icon: BarChart3 },
    { to: "/app/audit", label: "Audit Logs", icon: ScrollText },
  ],
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = NAV[user?.role] || NAV.professional;

  const doLogout = async () => { await logout(); navigate("/"); };

  const SideContent = () => (
    <>
      <div className="flex items-center gap-2.5 px-4 h-16 border-b border-slate-200">
        <img src={CYRA_LOGO} alt="CYRA" className="h-9 w-auto" />
        <div className="h-6 w-px bg-slate-300" />
        <div className="font-serif italic text-krida-crimson text-2xl leading-none">Krida</div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive
                  ? "bg-krida-crimson/15 text-krida-crimson border border-krida-crimson/30"
                  : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"
              }`
            }
          >
            <item.icon className="w-4.5 h-4.5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="flex items-center gap-3 px-2 py-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-krida-crimson to-krida-violet grid place-items-center font-heading font-bold text-sm">
            {(user?.name || "K")[0]}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="overline text-[9px] text-slate-500 capitalize">{user?.role}</div>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-krida-crimson hover:bg-white/5 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-krida-bg flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col fixed inset-y-0 bg-krida-surface/70 backdrop-blur-xl border-r border-white/5 z-40">
        <SideContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-krida-surface border-r border-white/5">
            <SideContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b border-white/5 bg-krida-bg/80 backdrop-blur-xl sticky top-0 z-30">
          <button className="lg:hidden text-slate-300" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex items-center gap-2 text-slate-500 font-mono text-xs">
            <Activity className="w-4 h-4 text-krida-emerald animate-pulse" />
            <span className="hidden sm:inline">DISCOVER → VERIFY → SKILL → DEPLOY → MANAGE → PAY → EVALUATE → PROGRESS</span>
            <span className="sm:hidden">KRIDA OS</span>
          </div>
          <div className="w-8" />
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
